/**
 * @fileoverview Hook for imperatively fetching live Streamline INFORMATION_SCHEMA data.
 * @module system-web/hooks/useLegacySchema
 *
 * Responsibilities:
 * - Fetch all base tables for a given Streamline legacy database (online exception).
 * - Fetch columns for a specific table on demand (used by AddRelationshipModal dropdowns).
 * - Provide loading / error / refresh state for the Manifest Editor.
 *
 * Dependencies:
 * - legacyManifestService (fetchLegacyTables, fetchLegacyColumns, fetchDeclaredFks)
 *
 * Architecture:
 * - Online exception: NO Dexie reads or writes.  All data is held in component state
 *   for the editor session only.
 * - fetchTables() and refreshTables() are imperative calls — NEVER called inside liveQuery.
 * - fetchColumnsFor() caches columns by table name in a local Map so repeated lookups are
 *   cheap without an extra network round-trip.
 *
 * Notes:
 * - Call loadTables() on mount or when legacyDb changes.
 * - Call fetchColumnsFor(table) from modal open handlers, not from render.
 */

import { useState, useCallback, useRef } from 'react';
import {
  fetchLegacyTables,
  fetchLegacyColumns,
  fetchDeclaredFks,
  type SchemaTable,
  type SchemaColumn,
  type DeclaredFk,
} from '../services/legacyManifestService';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract LegacySchemaState
 *
 * Purpose:
 * - Shape returned by useLegacySchema.
 *
 * Fields:
 * - tables: All base tables in the legacy database (empty until loadTables resolves).
 * - columnsCache: Map of table name → SchemaColumn[]. Populated incrementally.
 * - declaredFks: All declared FK relationships (typically empty for Streamline).
 * - isLoadingTables: True while the initial table fetch is in-flight.
 * - isLoadingFks: True while the declared-FK fetch is in-flight.
 * - tablesError: Non-null if the table fetch failed.
 * - fksError: Non-null if the FK fetch failed.
 * - loadTables: Imperatively trigger a (re)fetch of tables and FKs.
 * - fetchColumnsFor: Return columns for a given table; cached after first fetch.
 *
 * Rules:
 * - Never call loadTables or fetchColumnsFor inside a liveQuery observed function.
 *
 * Sync:
 * - Online exception; not persisted to Dexie.
 */
export interface LegacySchemaState {
  tables: SchemaTable[];
  columnsCache: Map<string, SchemaColumn[]>;
  declaredFks: DeclaredFk[];
  isLoadingTables: boolean;
  isLoadingFks: boolean;
  tablesError: string | null;
  fksError: string | null;
  loadTables: () => Promise<void>;
  fetchColumnsFor: (table: string) => Promise<SchemaColumn[]>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useLegacySchema
 *
 * Purpose:
 * - Provide imperative live-schema access (INFORMATION_SCHEMA) for the Manifest Editor.
 *
 * Returns:
 * - See LegacySchemaState.
 *
 * Side Effects:
 * - loadTables() calls fetchLegacyTables and fetchDeclaredFks via apiFetch (online exception).
 * - fetchColumnsFor() calls fetchLegacyColumns and caches the result in columnsCache.
 *
 * Offline Behavior:
 * - All fetches require connectivity. Errors are surfaced via tablesError / fksError.
 *
 * @errorHandling
 * Strategy:   Catches and converts thrown errors to error state strings.
 * User Impact: Manifest Editor shows inline error banners.
 * Logging:    Logs to console with legacyDb + operation context.
 */
export function useLegacySchema(legacyDb: string | null): LegacySchemaState {
  const [tables, setTables] = useState<SchemaTable[]>([]);
  const [declaredFks, setDeclaredFks] = useState<DeclaredFk[]>([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isLoadingFks, setIsLoadingFks] = useState(false);
  const [tablesError, setTablesError] = useState<string | null>(null);
  const [fksError, setFksError] = useState<string | null>(null);

  // columnsCache: table → SchemaColumn[]. Stable ref so callers get the same Map object.
  const columnsCacheRef = useRef<Map<string, SchemaColumn[]>>(new Map());
  // Force re-renders when cache changes by tracking a version counter.
  const [, setCacheVersion] = useState(0);

  const loadTables = useCallback(async (): Promise<void> => {
    if (!legacyDb) return;

    // Reset previous errors and clear caches for a fresh load.
    setTablesError(null);
    setFksError(null);
    columnsCacheRef.current.clear();
    setCacheVersion((v) => v + 1);

    // Fetch tables and declared FKs concurrently.
    setIsLoadingTables(true);
    setIsLoadingFks(true);

    const [tablesResult, fksResult] = await Promise.allSettled([
      fetchLegacyTables(legacyDb),
      fetchDeclaredFks(legacyDb),
    ]);

    setIsLoadingTables(false);
    setIsLoadingFks(false);

    if (tablesResult.status === 'fulfilled') {
      setTables(tablesResult.value);
    } else {
      const msg =
        tablesResult.reason instanceof Error
          ? tablesResult.reason.message
          : 'Could not load schema tables — check legacy DB connection.';
      console.error('[useLegacySchema] fetchLegacyTables failed:', tablesResult.reason);
      setTablesError(msg);
      setTables([]);
    }

    if (fksResult.status === 'fulfilled') {
      setDeclaredFks(fksResult.value);
    } else {
      const msg =
        fksResult.reason instanceof Error
          ? fksResult.reason.message
          : 'Could not load declared FKs.';
      console.error('[useLegacySchema] fetchDeclaredFks failed:', fksResult.reason);
      setFksError(msg);
      setDeclaredFks([]);
    }
  }, [legacyDb]);

  const fetchColumnsFor = useCallback(
    async (table: string): Promise<SchemaColumn[]> => {
      if (!legacyDb) return [];
      const cached = columnsCacheRef.current.get(table);
      if (cached) return cached;

      try {
        const cols = await fetchLegacyColumns(legacyDb, table);
        columnsCacheRef.current.set(table, cols);
        setCacheVersion((v) => v + 1);
        return cols;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(`[useLegacySchema] fetchLegacyColumns failed for ${table}:`, err);
        throw new Error(`Could not load columns for ${table}: ${msg}`);
      }
    },
    [legacyDb],
  );

  return {
    tables,
    columnsCache: columnsCacheRef.current,
    declaredFks,
    isLoadingTables,
    isLoadingFks,
    tablesError,
    fksError,
    loadTables,
    fetchColumnsFor,
  };
}
